import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

const MIDAS_APY_URL = 'https://api-prod.midas.app/api/data/apys'

/**
 * Midas LYT (liquid yield token) intrinsic APRs, from Midas's own APY
 * endpoint.
 *
 * The endpoint sits behind a WAF that returns **HTTP 403 with a `{}`
 * body** to clients without browser-ish headers (observed 2026-08-03)
 * — which the previous bare `fetch().json()` swallowed into a silent
 * empty result, wiping every Midas intrinsic yield. Hence the header
 * set, the `res.ok` check and the empty-payload throw below: a blocked
 * fetch must surface through `safeFetch` logging, not masquerade as
 * "no yields".
 *
 * Values are decimal APYs and the feed carries garbage for some keys
 * (`mwin` = 4.5e103, `splusd` = −0.9975 observed), so only values in
 * (0, 5] (≤ 500 % APY — the leveraged carry products run ~70 %) are
 * emitted.
 */

/**
 * Midas APY key → token-list `assetGroup` keys (copied
 * character-for-character; the lending join reads these). `keys` is an
 * array because several tokens have per-chain casing/name splits (e.g.
 * `Midas mBASIS::MBASIS` on Ethereum vs `Midas mBASIS::mBASIS` on
 * Plume, and Plasma's *reversed* `mHYPER::Midas Hyperithm`) — every
 * anchored variant is emitted until the token-lists GROUP_ALIAS
 * cleanup lands.
 *
 * Only tokens present in our token lists are mapped. API keys with no
 * list presence (msl, turtlepst, liquidreserve/liquidrwa, sgold,
 * mportofino, mkralpha, mtu, qhvnusd, the sol-, hype- and t-prefixed
 * rows, tacton) are deliberately absent. `bondusd`/`bondeth`/`bondbtc` are also
 * skipped: the only list rows with those tickers are Plaza Finance's
 * (`Plaza BondETH::BONDETH`) or of unverified issuer (`Bond
 * USD::BONDUSD`) — mapping them would bless a different project's
 * token with Midas's yield.
 */
const MIDAS_TOKENS: { apyKey: string; keys: string[] }[] = [
  { apyKey: 'mtbill', keys: ['Midas mTBILL::MTBILL'] },
  {
    apyKey: 'mbasis',
    keys: ['Midas mBASIS::MBASIS', 'Midas mBASIS::mBASIS'],
  },
  {
    apyKey: 'mbtc',
    keys: ['Midas mBTC::MBTC', 'Midas BTC Yield Token::mBTC'],
  },
  { apyKey: 'medge', keys: ['Midas mEDGE::MEDGE', 'Midas mEDGE::mEDGE'] },
  {
    apyKey: 'mre7',
    keys: ['Midas mRe7YIELD::MRE7YIELD', 'Midas Re7 Yield::mRe7YIELD'],
  },
  { apyKey: 'mre7sol', keys: ['Midas mRe7SOL::MRE7SOL'] },
  { apyKey: 'mre7eth', keys: ['Midas Re7 Ethereum::mRe7ETH'] },
  { apyKey: 'mmev', keys: ['Midas mMEV::MMEV'] },
  { apyKey: 'mfone', keys: ['Midas Fasanara ONE::mF-ONE'] },
  {
    apyKey: 'mhyper',
    keys: ['Midas Hyperithm::mHYPER', 'mHYPER::Midas Hyperithm'],
  },
  { apyKey: 'mapollo', keys: ['Midas mAPOLLO::MAPOLLO'] },
  // NB Avalanche carries a `MEV BTC::mevBTC` row (0x1f8e769b…) that MAY be
  // this asset, but the issuer is unverifiable (Midas's docs/API are
  // Cloudflare-walled) — deliberately not keyed, same rule as bondusd.
  { apyKey: 'mevbtc', keys: ['Midas mevBTC::MEVBTC'] },
  { apyKey: 'mfarm', keys: ['Midas mFARM::MFARM'] },
  { apyKey: 'msyrupusd', keys: ['Midas msyrupUSD::MSYRUPUSD'] },
  { apyKey: 'msyrupusdp', keys: ['Midas msyrupUSDp::MSYRUPUSDP'] },
  { apyKey: 'mre7btc', keys: ['Midas mRe7BTC::MRE7BTC'] },
  { apyKey: 'mxrp', keys: ['Midas XRP::mXRP'] },
  { apyKey: 'mevusd', keys: ['Midas Everstake USD::MEVUSD'] },
  { apyKey: 'mhypereth', keys: ['Midas Hyperithm ETH::mHyperETH'] },
  {
    apyKey: 'mhyperbtc',
    keys: ['Midas Hyperithm BTC::mHyperBTC', 'Midas Hyperithm BTC::MHYPERBTC'],
  },
  { apyKey: 'mm1usd', keys: ['Midas M1 USD Market Neutral::mM1-USD'] },
  // 2026-08 sweep additions
  { apyKey: 'mrox', keys: ['Midas Rockaway Market Neutral::MROX'] },
  { apyKey: 'mglobal', keys: ['Midas Fasanara Global::MGLOBAL'] },
  { apyKey: 'mglo', keys: ['Midas Fasanara Global Open::MGLO'] },
  { apyKey: 'weeur', keys: ['Liquid Euro::weEUR'] },
  { apyKey: 'plusd', keys: ['Plasma USD::plUSD'] },
  { apyKey: 'splusd', keys: ['Staked Plasma USD::splUSD'] },
]

/** Decimal-APY sanity band — see the header note. */
const MAX_DECIMAL_APY = 5

export const midasFetcher: YieldFetcher = {
  label: 'MIDAS',
  fetch: async () => {
    const res = await fetch(MIDAS_APY_URL, {
      headers: {
        accept: 'application/json',
        // WAF: bare fetches get 403 + `{}` — see header note.
        'user-agent':
          'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0 Safari/537.36',
        origin: 'https://midas.app',
        referer: 'https://midas.app/',
      },
      signal: AbortSignal.timeout(8000),
    })
    if (!res.ok) throw new Error(`Midas APY HTTP ${res.status}`)
    const apys: Record<string, number> = await res.json()
    if (!apys || Object.keys(apys).length === 0) {
      throw new Error('Midas APY payload empty (WAF block?)')
    }

    const result: Record<string, number> = {}
    for (const { apyKey, keys } of MIDAS_TOKENS) {
      const apy = apys[apyKey]
      if (typeof apy === 'number' && apy > 0 && apy <= MAX_DECIMAL_APY) {
        const apr = apyToAprPercent(apy * 100)
        for (const key of keys) result[key] = apr
      }
    }
    return result
  },
}

/** Exported for tests. */
export const MIDAS_TOKEN_TABLE = MIDAS_TOKENS
